import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../Config';
import { Search, Filter, ShoppingCart, Plus, Minus, X, Package, AlertCircle, CheckCircle } from 'lucide-react';

// Composant Toast
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 4000);

    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-emerald-500' : 
                  type === 'error' ? 'bg-rose-500' : 
                  'bg-blue-500';

  return (
    <div className={`fixed top-6 right-6 z-50 px-4 py-3 ${bgColor} text-white rounded-lg shadow-lg transform transition-transform duration-300 animate-slide-in`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          {type === 'success' && (
            <CheckCircle className="w-5 h-5 mr-2" />
          )}
          {type === 'error' && (
            <AlertCircle className="w-5 h-5 mr-2" />
          )}
          <span className="font-medium">{message}</span>
        </div>
        <button 
          onClick={onClose}
          className="ml-4 text-white hover:text-gray-200 focus:outline-none"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// Composant de confirmation
const ConfirmDialog = ({ isOpen, title, message, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-black/40"
        onClick={onCancel}
      />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="mt-2 text-gray-600 text-sm">{message}</p>
        </div>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
          >
            Confirmer
          </button>
        </div>
      </div>
    </div>
  );
};

export default function CreateDemande() {
  const [materiels, setMateriels] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [confirm, setConfirm] = useState({ 
    show: false, 
    title: '', 
    message: '', 
    onConfirm: null 
  });
  const navigate = useNavigate();

  const token = localStorage.getItem('token');
  const headers = { 
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ ...toast, show: false });
  };

  const showConfirm = (title, message, onConfirm) => {
    setConfirm({ show: true, title, message, onConfirm });
  };

  const hideConfirm = () => {
    setConfirm({ ...confirm, show: false });
  };

  useEffect(() => {
    fetchMateriels();
    fetchCategories();
  }, []);

  const fetchMateriels = async () => {
    try {
      const response = await fetch(`${API_URL}/materiels?per_page=100`, { headers });
      const data = await response.json();
      setMateriels(data.data?.data || data.data || []);
    } catch (err) {
      console.error('Erreur:', err);
      showToast('Erreur lors du chargement des matériels', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/categories`, { headers });
      const data = await response.json();
      setCategories(data.data || []);
    } catch (err) {
      console.error('Erreur catégories:', err);
    }
  };

  const addToCart = (materiel) => {
    const exist = cart.find(item => item.id === materiel.id);
    if (exist) {
      if (exist.quantite >= materiel.stock) {
        showToast(`Quantité maximale atteinte: ${materiel.stock} ${materiel.unite}`, 'error');
        return;
      }
      setCart(cart.map(item =>
        item.id === materiel.id
          ? { ...item, quantite: item.quantite + 1 }
          : item
      ));
      showToast(`${materiel.nom} ajouté au panier`, 'success');
    } else {
      setCart([...cart, {
        id: materiel.id,
        nom: materiel.nom,
        unite: materiel.unite,
        quantite: 1,
        stock: materiel.stock,
        category: materiel.category?.nom
      }]);
      showToast(`${materiel.nom} ajouté au panier`, 'success');
    }
  };

  const updateQty = (id, qty) => {
    const item = cart.find(item => item.id === id);
    if (!item) return;
    
    if (qty < 1) {
      setCart(cart.filter(item => item.id !== id));
      showToast('Article retiré du panier', 'success');
    } else if (qty > item.stock) {
      showToast(`Quantité maximale: ${item.stock} ${item.unite}`, 'error');
    } else {
      setCart(cart.map(item => item.id === id ? { ...item, quantite: qty } : item));
    }
  };

  const removeFromCart = (id) => {
    const item = cart.find(item => item.id === id);
    if (item) {
      setCart(cart.filter(item => item.id !== id));
      showToast(`${item.nom} retiré du panier`, 'success');
    }
  };

  const submitDemande = async () => {
    if (cart.length === 0) {
      showToast('Veuillez ajouter au moins un matériel', 'error');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/demandes`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          materiels: cart.map(item => ({ id: item.id, quantite: item.quantite }))
        })
      });

      if (!response.ok) throw new Error('Erreur envoi');

      showToast('Demande envoyée avec succès', 'success');
      setTimeout(() => {
        navigate('/mes-demandes');
      }, 1500);
    } catch (err) {
      showToast('Erreur lors de l\'envoi de la demande', 'error');
      console.error('Erreur:', err);
    }
  };

  const clearCart = () => {
    if (cart.length === 0) return;
    
    showConfirm(
      'Vider le panier',
      `Êtes-vous sûr de vouloir vider le panier ? ${cart.length} article${cart.length > 1 ? 's' : ''} seront supprimés.`,
      () => {
        setCart([]);
        showToast('Panier vidé', 'success');
        hideConfirm();
      }
    );
  };

  // Filtrer les matériels
  const filteredMateriels = materiels.filter(materiel => {
    const matchesSearch = searchTerm === '' || 
      materiel.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      materiel.category?.nom?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === '' || 
      materiel.category_id == selectedCategory;
    
    const isActive = materiel.actif && materiel.stock > 0;
    
    return matchesSearch && matchesCategory && isActive;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des matériels...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Toast Notification */}
      {toast.show && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={hideToast}
        />
      )}

      {/* Confirmation Dialog */}
      {confirm.show && (
        <ConfirmDialog
          isOpen={confirm.show}
          title={confirm.title}
          message={confirm.message}
          onConfirm={confirm.onConfirm}
          onCancel={hideConfirm}
        />
      )}

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-2">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Nouvelle demande</h1>
              <p className="text-gray-500 mt-1">Sélectionnez les matériels nécessaires</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <div className={`absolute -top-2 -right-2 w-5 h-5 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center ${
                  cart.length === 0 ? 'hidden' : ''
                }`}>
                  {cart.length}
                </div>
                <ShoppingCart className="w-6 h-6 text-gray-400" />
              </div>
            </div>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-semibold text-gray-900">{materiels.length}</div>
              <div className="text-sm text-gray-500">Matériels disponibles</div>
            </div>
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-semibold text-emerald-600">
                {materiels.filter(m => m.stock > 0).length}
              </div>
              <div className="text-sm text-gray-500">En stock</div>
            </div>
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-semibold text-amber-600">
                {materiels.filter(m => m.stock <= m.stock_minimum && m.stock > 0).length}
              </div>
              <div className="text-sm text-gray-500">Stock faible</div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Section matériels */}
          <div className="lg:col-span-2">
            {/* Filtres */}
            <div className="mb-6 bg-white p-4 rounded-lg border">
              <div className="text-sm font-medium text-gray-700 mb-3">Rechercher des matériels</div>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Rechercher un matériel..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div className="relative w-full sm:w-64">
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Toutes catégories</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.nom} ({cat.materiels_count || 0})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Liste matériels */}
            <div className="bg-white rounded-xl border overflow-hidden">
              <div className="px-6 py-4 border-b">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Matériels disponibles</h2>
                    <p className="text-sm text-gray-500 mt-1">
                      {filteredMateriels.length} matériel{filteredMateriels.length > 1 ? 's' : ''} trouvé{filteredMateriels.length > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="divide-y">
                {filteredMateriels.length === 0 ? (
                  <div className="px-6 py-12 text-center">
                    <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">Aucun matériel trouvé</p>
                    {(searchTerm || selectedCategory) && (
                      <button
                        onClick={() => {
                          setSearchTerm('');
                          setSelectedCategory('');
                        }}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        Réinitialiser les filtres
                      </button>
                    )}
                  </div>
                ) : (
                  filteredMateriels.map(materiel => {
                    const inCart = cart.find(item => item.id === materiel.id);
                    const stockStatus = materiel.stock <= materiel.stock_minimum;
                    
                    return (
                      <div key={materiel.id} className="px-6 py-4 hover:">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-medium text-gray-900">{materiel.nom}</h3>
                              {stockStatus && (
                                <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-800 rounded-full">
                                  Stock faible
                                </span>
                              )}
                              {materiel.stock === 0 && (
                                <span className="px-2 py-0.5 text-xs bg-rose-100 text-rose-800 rounded-full">
                                  Épuisé
                                </span>
                              )}
                            </div>
                            
                            <div className="text-sm text-gray-600 space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="text-gray-500">Catégorie:</span>
                                <span className="font-medium">{materiel.category?.nom || '-'}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-gray-500">Stock:</span>
                                <span className={`font-medium ${
                                  materiel.stock === 0 ? 'text-rose-600' :
                                  stockStatus ? 'text-amber-600' : 'text-emerald-600'
                                }`}>
                                  {materiel.stock} {materiel.unite}
                                </span>
                              </div>
                              {inCart && (
                                <div className="text-sm text-blue-600">
                                  <span className="font-medium">Dans votre panier:</span> {inCart.quantite} {materiel.unite}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <button
                            onClick={() => addToCart(materiel)}
                            disabled={materiel.stock === 0 || (inCart && inCart.quantite >= materiel.stock)}
                            className="ml-4 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                          >
                            {materiel.stock === 0 ? 'Épuisé' : 'Ajouter'}
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Panier */}
          <div>
            <div className="bg-white rounded-xl border overflow-hidden">
              <div className="px-6 py-4 border-b">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Votre demande</h2>
                    <p className="text-sm text-gray-500 mt-1">
                      {cart.length} article{cart.length > 1 ? 's' : ''}
                    </p>
                  </div>
                  {cart.length > 0 && (
                    <button
                      onClick={clearCart}
                      className="text-sm text-rose-600 hover:text-rose-800"
                    >
                      Vider
                    </button>
                  )}
                </div>
              </div>
              
              <div className="divide-y max-h-[500px] overflow-y-auto">
                {cart.length === 0 ? (
                  <div className="px-6 py-12 text-center">
                    <ShoppingCart className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">Panier vide</p>
                    <p className="text-sm text-gray-400">
                      Ajoutez des matériels depuis la liste
                    </p>
                  </div>
                ) : (
                  cart.map(item => (
                    <div key={item.id} className="px-6 py-4 hover:">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{item.nom}</div>
                          <div className="text-sm text-gray-500 mt-1">{item.unite}</div>
                          {item.category && (
                            <div className="text-xs text-gray-400 mt-1">{item.category}</div>
                          )}
                        </div>
                        
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="ml-2 p-1 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <div className="flex items-center justify-between mt-3">
                        <div className="text-sm text-gray-600">
                          Max: <span className="font-medium">{item.stock} {item.unite}</span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateQty(item.id, item.quantite - 1)}
                            className="w-7 h-7 flex items-center justify-center border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-100"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          
                          <input
                            type="number"
                            min="1"
                            max={item.stock}
                            value={item.quantite}
                            onChange={(e) => updateQty(item.id, parseInt(e.target.value) || 1)}
                            className="w-14 px-2 py-1 border border-gray-300 rounded-lg text-center text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                          
                          <button
                            onClick={() => updateQty(item.id, item.quantite + 1)}
                            disabled={item.quantite >= item.stock}
                            className="w-7 h-7 flex items-center justify-center border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {/* Actions */}
              {cart.length > 0 && (
                <div className="px-6 py-4 border-t">
                  <div className="mb-4 space-y-2">
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Articles:</span>
                      <span className="font-medium">{cart.length}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Total quantité:</span>
                      <span className="font-medium">
                        {cart.reduce((sum, item) => sum + item.quantite, 0)}
                      </span>
                    </div>
                  </div>
                  
                  <button
                    onClick={submitDemande}
                    className="w-full px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Envoyer la demande
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
}